import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { api } from '../../../lib/api';
import ContactCard from '../../../components/crm/ContactCard';
import ContactForm from '../../../components/crm/ContactForm';
import EmptyState from '../../../components/crm/EmptyState';
import UserForm from '../../../components/admin/UserForm';
import { InviteNotice } from '../../../components/admin/SendPasswordLinkDialog';

export default function ContactsTab({ account }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Emails that already have a portal login: those cards show "Has login"
  // instead of "Add as user".
  const [loginEmails, setLoginEmails] = useState(() => new Set());
  const [addingUser, setAddingUser] = useState(null); // contact → UserForm seed
  const [inviteNotice, setInviteNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/accounts/${account.id}/contacts`);
      setContacts(res.contacts || []);
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    api.get(`/api/admin/users?account=${encodeURIComponent(account.id)}`)
      .then((res) => { if (alive) setLoginEmails(new Set((res?.users || []).map((u) => String(u.email || '').toLowerCase()).filter(Boolean))); })
      .catch(() => {});
    return () => { alive = false; };
  }, [account.id]);

  const onUserSaved = (savedUser, invite) => {
    if (savedUser?.email) setLoginEmails((prev) => new Set([...prev, String(savedUser.email).toLowerCase()]));
    setAddingUser(null);
    if (invite) setInviteNotice({ email: savedUser.email, result: invite });
  };

  const onSaved = (c) => {
    setContacts((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id);
      if (idx === -1) return [c, ...prev];
      const copy = prev.slice();
      copy[idx] = c;
      return copy;
    });
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete contact ${c.name}?`)) return;
    await api.delete(`/api/admin/contacts/${c.id}`);
    setContacts((prev) => prev.filter((x) => x.id !== c.id));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-gray-500">{contacts.length} contact{contacts.length === 1 ? '' : 's'}</div>
        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900"
        >
          <Plus className="w-3 h-3" /> Add contact
        </button>
      </div>

      {!loading && contacts.length === 0 ? (
        <EmptyState icon={Users} title="No contacts yet" description="Add the principal, parent coordinator, or any key stakeholder." />
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              onEdit={(x) => { setEditing(x); setFormOpen(true); }}
              onDelete={remove}
              hasLogin={!!c.email && loginEmails.has(String(c.email).toLowerCase())}
              onAddUser={(x) => setAddingUser(x)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <ContactForm
          open
          accountId={account.id}
          accountType={account.type}
          initial={editing}
          onClose={() => setFormOpen(false)}
          onSaved={(c) => { onSaved(c); setFormOpen(false); }}
        />
      )}

      {/* "Add as user": the same form the Users tab uses, pre-filled from the
          contact and pinned to this school; the new user gets an invite link. */}
      <UserForm
        open={Boolean(addingUser)}
        onClose={() => setAddingUser(null)}
        onSaved={onUserSaved}
        initial={addingUser ? { name: addingUser.name || '', email: addingUser.email || '', role: 'client', accountId: account.id } : null}
        lockedAccount={{ id: account.id, name: account.name }}
      />
      <InviteNotice notice={inviteNotice} onClose={() => setInviteNotice(null)} />
    </div>
  );
}
